// A club site's own session, handed over by the shared sign-in site.
//
// Each club site ({club}.unionclubs.com) is a public OAuth client of Supabase
// Auth's OAuth 2.1 server (authorization code + PKCE). Members sign in once at
// the sign-in site; every club site then gets its own access and refresh
// tokens, stored only on that club's origin. See docs/adr/0009-shared-sign-in.md.

import type { PublicClub } from "@union/contracts";
import { useSyncExternalStore } from "react";

const SUPABASE = import.meta.env.VITE_SUPABASE_URL;
const STORAGE = "union.club-session";
const PENDING = "union.club-sign-in"; // sessionStorage: PKCE verifier + state
const CALLBACK = "/auth/callback";

interface Stored {
  clientId: string;
  accessToken: string;
  refreshToken: string;
  /** ms since epoch */
  expiresAt: number;
}

export type ClubSignInState = "signed-in" | "redirecting" | "no-such-club" | "offline" | "failed";

// --- Store (one per origin, shared by every component) -----------------------

let current: Stored | null = read();
let snapshot: { access_token: string } | null = current ? { access_token: current.accessToken } : null;
const listeners = new Set<() => void>();

function read(): Stored | null {
  try {
    return JSON.parse(localStorage.getItem(STORAGE) ?? "null") as Stored | null;
  } catch {
    return null;
  }
}

function write(next: Stored | null) {
  current = next;
  snapshot = next ? { access_token: next.accessToken } : null;
  try {
    if (next) localStorage.setItem(STORAGE, JSON.stringify(next));
    else localStorage.removeItem(STORAGE);
  } catch {
    // Private mode etc.: the session lasts for this page only.
  }
  for (const listener of listeners) listener();
}

/** The club session, shaped like a Supabase session for the web-kit API helpers. */
export function useClubSession(): { access_token: string } | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => snapshot,
  );
}

// --- Tokens ---------------------------------------------------------------------

async function tokenRequest(params: Record<string, string>): Promise<Stored | "rejected" | "offline"> {
  let res: Response;
  try {
    res = await fetch(`${SUPABASE}/auth/v1/oauth/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(params),
    });
  } catch {
    return "offline";
  }
  if (!res.ok) return "rejected";
  const body = (await res.json()) as { access_token: string; refresh_token: string; expires_in: number };
  return {
    clientId: params.client_id ?? "",
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAt: Date.now() + body.expires_in * 1000,
  };
}

let refreshing: Promise<"ok" | "rejected" | "offline"> | null = null;
let timer: number | undefined;

/** Renew the session. One at a time: refresh tokens rotate, and reusing one revokes it. */
function refresh(): Promise<"ok" | "rejected" | "offline"> {
  refreshing ??= (async () => {
    const stored = current;
    if (!stored) return "rejected" as const;
    const result = await tokenRequest({
      grant_type: "refresh_token",
      client_id: stored.clientId,
      refresh_token: stored.refreshToken,
    });
    if (result === "offline") return "offline" as const;
    if (result === "rejected") {
      write(null);
      return "rejected" as const;
    }
    write(result);
    scheduleRefresh();
    return "ok" as const;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

function scheduleRefresh() {
  window.clearTimeout(timer);
  if (!current) return;
  const inMs = Math.max(5_000, current.expiresAt - Date.now() - 60_000);
  timer = window.setTimeout(() => {
    refresh().then((result) => {
      if (result === "offline") window.setTimeout(scheduleRefresh, 30_000);
    });
  }, inMs);
}

// --- Sign-in hand-off -------------------------------------------------------------

const base64url = (bytes: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

async function beginSignIn(clientId: string): Promise<"redirecting"> {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  const state = base64url(crypto.getRandomValues(new Uint8Array(16)));
  sessionStorage.setItem(
    PENDING,
    JSON.stringify({ verifier, state, clientId, returnTo: window.location.hash }),
  );
  const url = new URL(`${SUPABASE}/auth/v1/oauth/authorize`);
  url.search = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: `${window.location.origin}${CALLBACK}`,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    scope: "openid email",
  }).toString();
  window.location.assign(url.toString());
  return "redirecting";
}

async function finishSignIn(): Promise<ClubSignInState> {
  const params = new URLSearchParams(window.location.search);
  const pending = JSON.parse(sessionStorage.getItem(PENDING) ?? "null") as {
    verifier: string;
    state: string;
    clientId: string;
    returnTo: string;
  } | null;
  sessionStorage.removeItem(PENDING);
  const code = params.get("code");
  // The state must be the one we sent: otherwise someone else started this.
  if (!pending || !code || params.get("state") !== pending.state) return "failed";
  const result = await tokenRequest({
    grant_type: "authorization_code",
    client_id: pending.clientId,
    code,
    redirect_uri: `${window.location.origin}${CALLBACK}`,
    code_verifier: pending.verifier,
  });
  if (result === "offline") return "offline";
  if (result === "rejected") return "failed";
  write(result);
  scheduleRefresh();
  window.history.replaceState(null, "", `/${pending.returnTo}`);
  return "signed-in";
}

/**
 * Make sure this club site has a session: finish a hand-off, reuse or renew a
 * stored session, or start sign-in at the shared sign-in site.
 */
export async function ensureClubSession(slug: string): Promise<ClubSignInState> {
  if (window.location.pathname === CALLBACK) return finishSignIn();

  if (current) {
    if (current.expiresAt - Date.now() > 60_000) {
      scheduleRefresh();
      return "signed-in";
    }
    const renewed = await refresh();
    // Offline with an expired session: stay signed in on the device (scoring
    // works offline); API calls will say they can't reach Union.
    if (renewed !== "rejected") return "signed-in";
  }

  let club: PublicClub;
  try {
    const res = await fetch(new URL(`/public/clubs/${slug}`, import.meta.env.VITE_API_URL));
    if (res.status === 404) return "no-such-club";
    if (!res.ok) return "failed";
    club = (await res.json()) as PublicClub;
  } catch {
    return "offline";
  }
  if (!club.signInClientId) return "failed";
  return beginSignIn(club.signInClientId);
}

/** Start over at the shared sign-in site (e.g. the API said the session is no good). */
export async function signInAgain(slug: string) {
  write(null);
  await ensureClubSession(slug);
}

/** Sign out here and at the shared sign-in site (which ends the session everywhere). */
export function signOutEverywhere() {
  write(null);
  const back = encodeURIComponent(window.location.origin);
  window.location.assign(`${import.meta.env.VITE_AUTH_ORIGIN}/sign-out?return=${back}`);
}
